import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AxiosClient from '../../assets/AxiosClient';
import { useStateContext } from '../../Contexts/ContextProvider';

const ExamManager = () => {
  const { id } = useParams();
  const { SetNotification, API } = useStateContext();
  const questionRef = useRef(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState(['', '']);
  const [correctIndex, setCorrectIndex] = useState(0);
  const [examState, setExamState] = useState({ status: 'closed', current_question_index: 0, question_count: 0, question: null });

  const publicExamUrl = useMemo(() => `${API || 'https://api.velorates.org'}/exams/${id}`, [API, id]);

  const fetchQuestions = async () => {
    try {
      const response = await AxiosClient.get(`/sessions/${id}/exams`);
      setQuestions(response.data?.questions || []);
      setExamState(response.data?.state || { status: 'closed', current_question_index: 0, question_count: 0, question: null });
    } catch (error) {
      SetNotification('Error loading exam questions', 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const updateOption = (index, value) => {
    const next = [...options];
    next[index] = value;
    setOptions(next);
  };

  const addOption = () => setOptions((prev) => [...prev, '']);

  const removeOption = (index) => {
    setOptions((prev) => {
      if (prev.length <= 2) return prev;
      const next = prev.filter((_, itemIndex) => itemIndex !== index);
      setCorrectIndex((current) => Math.min(current, next.length - 1));
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const questionText = questionRef.current?.value.trim();
    const cleanedOptions = options.map((option) => option.trim()).filter(Boolean);

    if (!questionText) {
      SetNotification('Please enter the exam question', 'danger');
      return;
    }

    if (cleanedOptions.length < 2) {
      SetNotification('Please provide at least 2 options', 'danger');
      return;
    }

    if (correctIndex >= cleanedOptions.length) {
      SetNotification('Please select a valid correct answer', 'danger');
      return;
    }

    setSaving(true);

    try {
      await AxiosClient.post(`/sessions/${id}/exams/questions`, {
        question: questionText,
        options: cleanedOptions,
        correct_option_index: correctIndex,
      });

      SetNotification('Exam question created successfully', 'success');
      questionRef.current.value = '';
      setOptions(['', '']);
      setCorrectIndex(0);
      fetchQuestions();
    } catch (error) {
      const msg = error.response?.data?.message || 'Error creating exam question';
      SetNotification(msg, 'danger');
    } finally {
      setSaving(false);
    }
  };

  const openExam = async () => {
    try {
      await AxiosClient.post(`/sessions/${id}/exams/open`);
      SetNotification('Exam opened', 'success');
      fetchQuestions();
    } catch (error) {
      SetNotification(error.response?.data?.message || 'Could not open the exam', 'danger');
    }
  };

  const nextQuestion = async () => {
    try {
      await AxiosClient.post(`/sessions/${id}/exams/next`);
      SetNotification('Next question opened', 'success');
      fetchQuestions();
    } catch (error) {
      SetNotification(error.response?.data?.message || 'Could not advance the exam', 'danger');
    }
  };

  const closeExam = async () => {
    try {
      await AxiosClient.post(`/sessions/${id}/exams/close`);
      SetNotification('Exam closed', 'success');
      fetchQuestions();
    } catch (error) {
      SetNotification(error.response?.data?.message || 'Could not close the exam', 'danger');
    }
  };

  if (loading) return <h6 className="m-3">Loading exam...</h6>;

  return (
    <div className="m-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <div>
          <h3 className="mb-1">Exam management</h3>
          <small className="text-muted">Build the exam question bank for this session.</small>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <a href={publicExamUrl} target="_blank" rel="noreferrer" className="btn btn-primary">Open public exam</a>
          <Link to={`/sessions/${id}`} className="btn btn-outline-dark">Back to session</Link>
        </div>
      </div>

      <div className="card p-4 mb-4 border-0" style={{ background: 'linear-gradient(135deg, rgba(79,78,214,0.96), rgba(34,195,166,0.82))', color: '#fff', boxShadow: '0 18px 44px rgba(52, 55, 120, 0.18)' }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h5 className="mb-0 text-white">Exam status</h5>
          <div className="d-flex gap-2 flex-wrap">
            <button type="button" className="btn btn-sm btn-warning fw-bold" onClick={openExam} disabled={examState.status === 'open'}>
              Open exam
            </button>
            <button type="button" className="btn btn-sm btn-light fw-bold" onClick={nextQuestion} disabled={examState.status !== 'open'} style={{ color: '#2d254d' }}>
              Next question
            </button>
            <button type="button" className="btn btn-sm btn-outline-light fw-bold" onClick={closeExam} disabled={examState.status === 'closed'}>
              Close exam
            </button>
          </div>
        </div>

        <div className="mb-3">
          <span className={`badge ${examState.status === 'closed' ? 'bg-secondary' : examState.status === 'finished' ? 'bg-primary' : 'bg-success'}`} style={{ background: examState.status === 'closed' ? 'rgba(255,255,255,0.14)' : examState.status === 'finished' ? 'rgba(13,110,253,0.28)' : 'rgba(255,177,59,0.28)', color: '#fff' }}>
            {examState.status === 'closed' ? 'Closed' : examState.status === 'finished' ? 'Finished' : 'Open'}
          </span>
          {examState.question && (
            <div className="mt-3 text-white-50">
              <strong className="text-white">Current question:</strong> {examState.current_question_index + 1} / {examState.question_count}
              <div className="mt-2 text-white">{examState.question.question}</div>
            </div>
          )}
        </div>
      </div>

      <div className="card p-3 mb-4">
        <h5 className="mb-3">Add exam question</h5>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label">Question text</label>
            <input ref={questionRef} className="form-control" type="text" placeholder="Enter the exam question" required />
          </div>

          <div className="mb-3">
            <label className="form-label">Answer options</label>
            {options.map((option, index) => (
              <div key={index} className="d-flex gap-2 align-items-center mb-2">
                <input
                  type="text"
                  className="form-control"
                  value={option}
                  onChange={(event) => updateOption(index, event.target.value)}
                  placeholder={`Option ${index + 1}`}
                />
                <button
                  type="button"
                  className={`btn ${correctIndex === index ? 'btn-success' : 'btn-outline-success'}`}
                  onClick={() => setCorrectIndex(index)}
                >
                  {correctIndex === index ? 'Correct' : 'Mark correct'}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={() => removeOption(index)}
                  disabled={options.length <= 2}
                >
                  ×
                </button>
              </div>
            ))}
            <button type="button" className="btn btn-outline-secondary" onClick={addOption}>+ Add option</button>
          </div>

          <button type="submit" className="btn btn-dark" disabled={saving}>
            {saving ? 'Saving...' : 'Save question'}
          </button>
        </form>
      </div>

      <div className="card p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Current exam questions</h5>
          <Link to={`/sessions/${id}/exam/results`} className="btn btn-sm btn-outline-primary">Leaderboard</Link>
        </div>

        {!questions.length ? (
          <p className="text-muted mb-0">No questions were added yet.</p>
        ) : (
          <div className="list-group">
            {questions.map((question, index) => (
              <div key={question.id} className="list-group-item">
                <div className="fw-bold mb-2">{index + 1}. {question.question}</div>
                <ul className="mb-2">
                  {question.options.map((option, optionIndex) => (
                    <li key={`${question.id}-${optionIndex}`}>
                      {option}
                      {Number(question.correct_option_index) === optionIndex && (
                        <span className="ms-2 badge bg-success">Correct</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExamManager;
